import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTenantSettings } from '../hooks/useTenantSettings';
import { useTier } from '../context/TierContext';
import { UpgradePrompt } from '../components/FeatureGate';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Upload, 
  Download, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  Loader2,
  Factory,
  Truck,
  Users,
  Building2,
  MapPin,
  RefreshCw,
  ExternalLink,
  Info,
  History,
  FileText,
  Calendar,
  Contact
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DATA_TYPES = [
  {
    id: 'suppliers',
    name: 'Suppliers',
    description: 'Import your subcontractors and equipment providers',
    icon: Factory,
    order: 1
  },
  {
    id: 'equipment',
    name: 'Assets',
    description: 'Import your fleet (requires suppliers first)',
    icon: Truck,
    order: 2,
    dependsOn: 'suppliers'
  },
  {
    id: 'operators',
    name: 'Operators',
    description: 'Import your operators (requires suppliers first)',
    icon: Users,
    order: 3,
    dependsOn: 'suppliers'
  },
  {
    id: 'customers',
    name: 'Customers',
    description: 'Import from your accounting package for invoicing',
    icon: Building2,
    order: 4,
    isSpecial: true
  },
  {
    id: 'sites',
    name: 'Sites',
    description: 'Import job sites (requires customers first)',
    icon: MapPin,
    order: 5,
    dependsOn: 'customers'
  },
  {
    id: 'customer_contacts',
    name: 'Customer Contacts',
    description: 'Import contacts for your customers (requires customers first)',
    icon: Contact,
    order: 6,
    dependsOn: 'customers'
  }
];

export default function DataImport() {
  const { user, token } = useAuth();
  const { fmtDateTime } = useTenantSettings();
  const { hasFeature, loading: tierLoading } = useTier();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [validationResults, setValidationResults] = useState(null);
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [showCustomerDialog, setShowCustomerDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importHistory, setImportHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('import');

  // Check tier access
  const hasAccess = hasFeature('data_import');

  const fetchStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/import/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch import status:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/import/history?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setImportHistory(data.logs || []);
      }
    } catch (error) {
      console.error('Failed to fetch import history:', error);
    } finally {
      setHistoryLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory();
    }
  }, [activeTab, fetchHistory]);

  const downloadCombinedTemplate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/import/combined-template`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'equipr_import_template.xlsx';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Combined import template downloaded');
      } else {
        toast.error('Failed to download template');
      }
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleCombinedFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('Please upload a .xlsx file (download the combined template first)');
      event.target.value = '';
      return;
    }

    setSelectedFile(file);
    setValidating(true);
    setValidationResults(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/import/validate-combined`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const results = await response.json();
        setValidationResults(results);
        setShowValidationDialog(true);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Validation failed');
        setSelectedFile(null);
      }
    } catch (error) {
      toast.error('Failed to validate file');
      setSelectedFile(null);
    } finally {
      setValidating(false);
      event.target.value = '';
    }
  };

  const executeCombinedImport = async () => {
    if (!selectedFile) return;

    setImporting(true);

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await fetch(`${API_URL}/api/import/execute-combined`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          toast.success(`Imported ${result.total_imported} new and updated ${result.total_updated} existing records across ${result.sections.length} sheet${result.sections.length === 1 ? '' : 's'}`);
        } else {
          toast.warning(`Import completed with ${result.total_errors} errors. ${result.total_imported} new, ${result.total_updated} updated.`);
        }
        setShowValidationDialog(false);
        setValidationResults(null);
        setSelectedFile(null);
        fetchStatus();
        fetchHistory();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Import failed');
      }
    } catch (error) {
      toast.error('Failed to execute import');
    } finally {
      setImporting(false);
    }
  };

  const formatDate = (dateString) => {
    return fmtDateTime(dateString);
  };

  const getDataTypeIcon = (dataType) => {
    const icons = {
      suppliers: Factory,
      equipment: Truck,
      operators: Users,
      customers: Building2,
      sites: MapPin,
      customer_contacts: Contact
    };
    return icons[dataType] || FileText;
  };

  const isReady = (dataType) => {
    if (!status) return false;
    const typeStatus = status[dataType];
    return typeStatus?.ready !== false;
  };

  if (loading || tierLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Show upgrade prompt if no access
  if (!hasAccess) {
    return (
      <div className="space-y-6" data-testid="data-import-page">
        <div>
          <h1 className="text-2xl font-semibold">Data Import</h1>
          <p className="text-muted-foreground">Import your existing data into EQUIPR CORE</p>
        </div>
        
        <UpgradePrompt 
          feature="data_import"
          title="Upgrade to Access Data Import"
          description="Bulk import suppliers, equipment, operators, customers, and sites. Save hours of manual data entry with our powerful import tool."
        />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="data-import-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Data Import</h1>
          <p className="text-muted-foreground">Import your existing data into EQUIPR CORE</p>
        </div>
        <Button variant="outline" onClick={() => { fetchStatus(); if (activeTab === 'history') fetchHistory(); }}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="import" className="gap-2">
            <Upload className="h-4 w-4" />
            Import Data
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <History className="h-4 w-4" />
            Import History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="import" className="space-y-6 mt-6">
          {/* Recommended Order Info */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>One file. All your data.</AlertTitle>
            <AlertDescription className="mt-2">
              Download the combined template, fill in any of the sheets you need, then upload it back.
              Sheets are imported in dependency order (Suppliers & Customers first), so you can reference a
              brand-new supplier or customer from the Equipment / Sites / Operators / Contacts sheets in the
              same file. Equipment types must already exist in Data Management.
            </AlertDescription>
          </Alert>

          {/* Status Grid — at-a-glance counts per data type */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="import-status-grid">
            {DATA_TYPES.map((type) => {
              const Icon = type.icon;
              const typeStatus = status?.[type.id];
              const count = typeStatus?.count || 0;
              const ready = isReady(type.id);
              return (
                <div
                  key={type.id}
                  className={`p-3 rounded-lg border ${ready ? 'bg-card' : 'bg-muted/50'}`}
                  data-testid={`status-tile-${type.id}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{type.name}</span>
                  </div>
                  <div className="text-xl font-semibold">{count}</div>
                  {!ready && (
                    <div className="text-[11px] text-amber-600 mt-1">{typeStatus?.message}</div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Combined Import Card */}
          <Card data-testid="combined-import-card">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <FileText className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">Combined Import Template</CardTitle>
                  <CardDescription>
                    A single Excel workbook with one sheet per data type — Suppliers, Customers,
                    Equipment, Sites, Operators, and Customer Contacts.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="outline"
                  onClick={downloadCombinedTemplate}
                  data-testid="download-combined-template-btn"
                  className="sm:flex-1"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Combined Template (.xlsx)
                </Button>

                <div className="relative sm:flex-1">
                  <input
                    type="file"
                    accept=".xlsx"
                    onChange={handleCombinedFileSelect}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    disabled={validating}
                    data-testid="combined-file-input"
                  />
                  <Button
                    className="w-full"
                    disabled={validating}
                    data-testid="upload-combined-btn"
                  >
                    {validating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Validating…
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Filled Template
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Customers + accounting hint */}
              <div className="mt-4 p-3 rounded-md bg-muted/40 border border-dashed flex items-start gap-3">
                <ExternalLink className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div className="flex-1 text-sm">
                  <span className="font-medium">Customers from Xero / MYOB?</span>{' '}
                  <span className="text-muted-foreground">
                    For invoicing to work cleanly, sync customers from your accounting package instead of typing them into the Customers sheet.
                  </span>
                  <Button
                    variant="link"
                    size="sm"
                    className="px-1 h-auto"
                    onClick={() => setShowCustomerDialog(true)}
                    data-testid="open-accounting-dialog-btn"
                  >
                    Connect accounting
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-6">
          {historyLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : importHistory.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <History className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">No Import History</h3>
                <p className="text-muted-foreground mt-1">
                  Your import history will appear here after you complete your first import.
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Import History</CardTitle>
                <CardDescription>
                  View all past data imports performed by administrators
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {importHistory.map((log) => {
                    const Icon = getDataTypeIcon(log.data_type);
                    const hasErrors = log.error_count > 0;
                    
                    return (
                      <div 
                        key={log.id} 
                        className="flex items-start gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                        data-testid={`history-entry-${log.id}`}
                      >
                        <div className={`p-2 rounded-lg ${hasErrors ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-green-100 dark:bg-green-900/30'}`}>
                          <Icon className={`h-5 w-5 ${hasErrors ? 'text-amber-600' : 'text-green-600'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium capitalize">{log.data_type}</span>
                            <Badge variant={hasErrors ? 'outline' : 'secondary'} className={hasErrors ? 'border-amber-300 text-amber-700' : ''}>
                              {hasErrors ? 'Completed with errors' : 'Success'}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3.5 w-3.5" />
                              {formatDate(log.created_at)}
                            </span>
                            <span>by {log.imported_by_name}</span>
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-sm">
                            <span className="text-green-600">
                              <CheckCircle2 className="h-3.5 w-3.5 inline mr-1" />
                              {log.imported_count} new
                            </span>
                            <span className="text-blue-600">
                              <RefreshCw className="h-3.5 w-3.5 inline mr-1" />
                              {log.updated_count} updated
                            </span>
                            {log.error_count > 0 && (
                              <span className="text-red-600">
                                <XCircle className="h-3.5 w-3.5 inline mr-1" />
                                {log.error_count} errors
                              </span>
                            )}
                            <span className="text-muted-foreground">
                              {log.total_rows} total rows
                            </span>
                          </div>
                          {log.filename && (
                            <div className="mt-1 text-xs text-muted-foreground flex items-center gap-1">
                              <FileText className="h-3 w-3" />
                              {log.filename}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Combined Validation Results Dialog */}
      <Dialog open={showValidationDialog} onOpenChange={setShowValidationDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle>Import Preview</DialogTitle>
            <DialogDescription>
              Review the validation results across all sheets before importing.
            </DialogDescription>
          </DialogHeader>

          {validationResults && (
            <div className="space-y-4">
              {/* Top-level summary */}
              <div className="grid grid-cols-4 gap-3" data-testid="combined-summary">
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                  <div className="text-2xl font-bold text-green-600" data-testid="combined-new-count">{validationResults.total_new}</div>
                  <div className="text-sm text-muted-foreground">New</div>
                </div>
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
                  <div className="text-2xl font-bold text-blue-600" data-testid="combined-update-count">{validationResults.total_updates}</div>
                  <div className="text-sm text-muted-foreground">Updates</div>
                </div>
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-center">
                  <div className="text-2xl font-bold text-red-600" data-testid="combined-error-count">{validationResults.total_errors}</div>
                  <div className="text-sm text-muted-foreground">Errors</div>
                </div>
                <div className="p-3 bg-muted rounded-lg text-center">
                  <div className="text-2xl font-bold" data-testid="combined-total-rows">{validationResults.total_rows}</div>
                  <div className="text-sm text-muted-foreground">Total Rows</div>
                </div>
              </div>

              {/* Per-section breakdown */}
              <ScrollArea className="h-[400px] border rounded-lg">
                <div className="p-3 space-y-4">
                  {validationResults.sections?.map((section) => (
                    <div key={section.data_type} data-testid={`section-${section.data_type}`}>
                      <div className="flex items-center justify-between mb-2 sticky top-0 bg-background py-2 z-10 border-b">
                        <div className="font-semibold text-base">{section.sheet_name}</div>
                        <div className="flex items-center gap-2 text-xs">
                          {section.new_count > 0 && (
                            <Badge variant="secondary" className="bg-green-100 text-green-700 dark:bg-green-900/40">
                              {section.new_count} new
                            </Badge>
                          )}
                          {section.update_count > 0 && (
                            <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-900/40">
                              {section.update_count} update{section.update_count === 1 ? '' : 's'}
                            </Badge>
                          )}
                          {section.error_count > 0 && (
                            <Badge variant="destructive">{section.error_count} error{section.error_count === 1 ? '' : 's'}</Badge>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        {section.rows?.map((row) => (
                          <div
                            key={`${section.data_type}-${row.row}`}
                            className={`p-3 rounded-lg border ${
                              row.status === 'error'
                                ? 'bg-red-50 dark:bg-red-900/20 border-red-200'
                                : row.status === 'update'
                                ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200'
                                : 'bg-green-50 dark:bg-green-900/20 border-green-200'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className="mt-0.5">
                                {row.status === 'error' ? (
                                  <XCircle className="h-5 w-5 text-red-500" />
                                ) : row.status === 'update' ? (
                                  <AlertCircle className="h-5 w-5 text-blue-500" />
                                ) : (
                                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                                )}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">Row {row.row}</span>
                                  <Badge variant={row.status === 'error' ? 'destructive' : row.status === 'update' ? 'secondary' : 'default'}>
                                    {row.status === 'new' ? 'New' : row.status === 'update' ? 'Will Update' : 'Error'}
                                  </Badge>
                                </div>
                                {row.data && (
                                  <div className="text-sm text-muted-foreground mt-1">
                                    {Object.entries(row.data).filter(([k, v]) => v && !k.includes('_id')).slice(0, 3).map(([key, value]) => (
                                      <span key={key} className="mr-3">
                                        <span className="font-medium">{key}:</span> {String(value).substring(0, 30)}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {row.errors && (
                                  <div className="text-sm text-red-600 mt-1">
                                    {row.errors.map((err, i) => (
                                      <div key={`err-${section.data_type}-${row.row}-${i}`}>{err}</div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowValidationDialog(false)} data-testid="combined-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={executeCombinedImport}
              disabled={importing || !validationResults || validationResults.total_valid === 0}
              data-testid="combined-import-btn"
            >
              {importing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Import {validationResults?.total_valid || 0} Records
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Import Dialog */}
      <Dialog open={showCustomerDialog} onOpenChange={setShowCustomerDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Customers</DialogTitle>
            <DialogDescription>
              Customers should be imported from your accounting package to ensure invoicing works correctly.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {status?.customers?.has_integration ? (
              <Alert>
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <AlertTitle>Connected to {status.customers.integration_type === 'xero' ? 'Xero' : 'MYOB'}</AlertTitle>
                <AlertDescription>
                  Go to Settings → Accounting to sync your customers.
                </AlertDescription>
              </Alert>
            ) : (
              <>
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertTitle>No accounting package connected</AlertTitle>
                  <AlertDescription>
                    Connect Xero or MYOB to automatically sync your customers.
                  </AlertDescription>
                </Alert>
                
                <div className="grid grid-cols-2 gap-4">
                  <Button 
                    variant="outline" 
                    className="h-20 flex-col"
                    onClick={() => {
                      setShowCustomerDialog(false);
                      window.location.href = '/settings?tab=accounting';
                    }}
                  >
                    <img src="https://www.xero.com/content/dam/xero/images/logo/xero-logo-hires-RGB.png" alt="Xero" className="h-6 mb-2" />
                    Connect Xero
                  </Button>
                  <Button 
                    variant="outline"
                    className="h-20 flex-col"
                    onClick={() => {
                      setShowCustomerDialog(false);
                      window.location.href = '/settings?tab=accounting';
                    }}
                  >
                    <span className="font-bold text-lg mb-2">MYOB</span>
                    Connect MYOB
                  </Button>
                </div>
              </>
            )}
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  Or import manually
                </span>
              </div>
            </div>
            
            <p className="text-sm text-muted-foreground text-center">
              Don't use accounting software? Use the Customers sheet of the combined import template.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCustomerDialog(false)}>
              Close
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setShowCustomerDialog(false);
                downloadCombinedTemplate();
              }}
            >
              <Download className="h-4 w-4 mr-2" />
              Download Combined Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
